/**
 * StatusPopoverContext.tsx
 * -----------------------------------------------------------------------------
 * Context to manage status popover visibility across the Atlas.
 * Ensures only one status popover can be open at a time.
 */

import React, { createContext, useContext, useState, useCallback, useMemo, ReactNode } from 'react';
import { OverlayPanel } from 'primereact/overlaypanel';

interface StatusPopoverContextType {
  /** Register a popover ref and get notified when it should close */
  registerPopover: (id: string, ref: React.RefObject<OverlayPanel>) => void;
  /** Unregister a popover when component unmounts */
  unregisterPopover: (id: string) => void;
  /** Called when a popover is about to open - closes any other open popover */
  onPopoverOpen: (id: string) => void;
}

const StatusPopoverContext = createContext<StatusPopoverContextType | null>(null);

interface StatusPopoverProviderProps {
  children: ReactNode;
}

export const StatusPopoverProvider: React.FC<StatusPopoverProviderProps> = ({ children }) => {
  // Map of sphere IDs to their popover refs
  const [popoverRefs] = useState<Map<string, React.RefObject<OverlayPanel>>>(new Map());
  // Track currently open popover ID
  const [openPopoverId, setOpenPopoverId] = useState<string | null>(null);

  const registerPopover = useCallback((id: string, ref: React.RefObject<OverlayPanel>) => {
    popoverRefs.set(id, ref);
  }, [popoverRefs]);

  const unregisterPopover = useCallback((id: string) => {
    popoverRefs.delete(id);
    if (openPopoverId === id) {
      setOpenPopoverId(null);
    }
  }, [popoverRefs, openPopoverId]);

  const onPopoverOpen = useCallback((id: string) => {
    // Close currently open popover if different from the one being opened
    if (openPopoverId && openPopoverId !== id) {
      const currentRef = popoverRefs.get(openPopoverId);
      if (currentRef?.current) {
        currentRef.current.hide();
      }
    }
    setOpenPopoverId(id);
  }, [openPopoverId, popoverRefs]);

  // Memoize the context value to prevent unnecessary re-renders
  const contextValue = useMemo(
    () => ({ registerPopover, unregisterPopover, onPopoverOpen }),
    [registerPopover, unregisterPopover, onPopoverOpen]
  );

  return (
    <StatusPopoverContext.Provider value={contextValue}>
      {children}
    </StatusPopoverContext.Provider>
  );
};

/**
 * Hook to access status popover context
 * Returns null if used outside of provider (for backwards compatibility)
 */
export const useStatusPopoverContext = (): StatusPopoverContextType | null => {
  return useContext(StatusPopoverContext);
};
